import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "auction-table" }
const _hoisted_2 = ["onClick", "textContent"]
const _hoisted_3 = {
  key: 0,
  class: "click-indicator"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = {
  key: 0,
  class: "click-indicator"
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { style: {"white-space":"nowrap"} }
const _hoisted_8 = { style: {"font-size":"12px","white-space":"nowrap"} }
const _hoisted_9 = { class: "auction-table__subtle" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = ["onClick"]

import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount
} from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import { formatPrice } from '@/common/function'
import { getNameArtFromId } from '@/common/list'
import { useWebSocketStore } from '@/store/websocket.store'
import { copyText } from 'vue3-clipboard'
import Toast from 'primevue/toast'
import HistoryChart from '@/components/HistoryChart.vue'
import { calculateArtefactScore, artefactScoreTooltip } from '@/common/artefact-score'
import {
  ArtefactFilterSettings,
  DEFAULT_ARTEFACT_FILTER_SETTINGS,
  LiquidityWindow,
  getArtefactFilterReasons,
  getDailySalesRate,
  getRecentSales
} from '@/common/artefact-filters'

interface CurrentLot {
  price: number
  startTime: string
  endTime: string
  currentPrice: number
  additionalInfo: object | null
}

export interface TableRow {
  qlt: number
  ptn: string
  averagePrice: string | null
  historyMinPrice: number
  historySalePrices: HistoryLot[]
  currentPrice: number
  currentElems: CurrentLot[]
  profit: string | null
  commission: number | null
  salePrice: number | null
  startPrice: number | null
  startTime: string | null
  endTime: string | null
  isBid?: boolean
  additionalInfo: object | null
  sales24h?: number
  sales48h?: number
  rawProfit?: number | null
  minRequiredProfit?: number | null
}

interface HistoryLot {
  price: number
  time: string | Date
  additional: object | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ArtefactDashboard',
  props: {
    enteredProfit: { default: 0 },
    isBidVariant: { type: Boolean, default: false },
    onlyGoodLots: { type: Boolean, default: DEFAULT_ARTEFACT_FILTER_SETTINGS.onlyLiquid },
    liquidityWindow: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.liquidityWindow },
    minLiquiditySales: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales },
    minBuyoutProfit: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit }
  },
  setup(__props: any) {

const props = __props

const toast = ref()

const wsStore = useWebSocketStore()

const tableData = ref<any[]>([])

// const expandedRows = ref<Record<string, boolean>>({});

const qltMap: Record<number, string> = {
  0: '#E0E0E0',
  1: '#4CAF50',
  2: '#2196F3',
  3: '#9C27B0',
  4: '#F44336',
  5: '#FFD700'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0] ?? 'white'

function onArtefactProfitAll(data: any) {
  const now = Date.now()
  const next: any[] = []
  const filterSettings: ArtefactFilterSettings = {
    onlyLiquid: props.onlyGoodLots,
    liquidityWindow: props.liquidityWindow,
    minSales: props.minLiquiditySales,
    minProfit: props.minBuyoutProfit
  }

  for (const artefactId in data.artefactProfitMap) {
    const artefact = data.artefactProfitMap[artefactId]
    if (!artefact?.rows) continue

    const name = getNameArtFromId(artefactId)

    artefact.rows.forEach((row: any, index: number) => {
      const scoreDetails = calculateArtefactScore(row, props.liquidityWindow, now)
      const item = {
        ...row,
        score: scoreDetails.score,
        scoreDetails,
        name,
        id: `${artefactId}_${index}`
      }

      if (getArtefactFilterReasons(row, filterSettings, now).length) return

      item._dailySalesRate = getDailySalesRate(row, props.liquidityWindow, now)

      next.push(item)
    })
  }

  next.sort((a, b) => {
    return (
      b.score - a.score ||
      Number(b.rawProfit ?? b.profit) - Number(a.rawProfit ?? a.profit) ||
      b._dailySalesRate - a._dailySalesRate ||
      a.id.localeCompare(b.id)
    )
  })

  tableData.value = next.filter(i => !i.isBid)
}

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string, type: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })

  wsStore.sendArt({
    text,
    type
  })
}

function getAuctionTimer(startIso: string, endIso: string) {
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const now = Date.now()

  // const duration = end - start;
  const passed = Math.max(0, now - start)
  const left = Math.max(0, end - now)

  return {
    passedMs: passed,
    leftMs: left
  }
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)

  if (h > 0) return `${h}ч ${m % 60}м`
  if (m > 0) return `${m}м ${s % 60}с`
  return `${s}с`
}

function calculatedBid(data) {
  const firstElemPrice = data.currentElems[0]?.price

  let salePrice = firstElemPrice <= data.averagePrice ? firstElemPrice - 1 : data.averagePrice

  const commission = salePrice * 0.05
  const result = salePrice - props.enteredProfit - commission

  return Math.floor(result)
}

const isBid = ref(false)

const highlightMap = ref<Record<string, { adminId: number; until: number }>>({})

const highlightMapPrice = ref<Record<string, { adminId: number; until: number }>>({})

function onAdminClick(data) {
  if (data.type === 'name') {
    const item = tableData.value.find(i => i.name === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMap.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMap.value[item.id]
    }, duration)
  } else if (data.type === 'price') {
    const item = tableData.value.find(i => i.salePrice === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMapPrice.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMapPrice.value[item.id]
    }, duration)
  }
}

watch(
  [
    () => wsStore.artefactProfitData,
    () => props.onlyGoodLots,
    () => props.liquidityWindow,
    () => props.minLiquiditySales,
    () => props.minBuyoutProfit
  ],
  ([data]) => {
    onArtefactProfitAll(data)
  },
  { immediate: true }
)

onMounted(() => {
  wsStore.connect() // 👈 ВАЖНО

  wsStore.artefactSocket?.on('admin_click', onAdminClick)

  isBid.value = localStorage.getItem('bid') === 'true'
})

onBeforeUnmount(() => {
  wsStore.artefactSocket?.off('admin_click', onAdminClick)
  tableData.value = []
})

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Toast), {
      ref_key: "toast",
      ref: toast
    }, null, 512),
    _createVNode(_unref(DataTable), {
      value: tableData.value,
      dataKey: "id",
      responsiveLayout: "scroll",
      sortField: "score",
      sortOrder: -1
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Column), {
          field: "name",
          header: "Артефакт"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", {
              class: _normalizeClass({
              'row-highlight': highlightMap.value[data.id]
            }),
              style: {"white-space":"nowrap","position":"relative"}
            }, [
              _createElementVNode("span", {
                onClick: ($event: any) => (copyToClipboard(data.name, 'name')),
                textContent: _toDisplayString(data.name)
              }, null, 8, _hoisted_2),
              _cache[0] || (_cache[0] = _createTextVNode("  ", -1)),
              _createElementVNode("span", {
                style: _normalizeStyle([{"white-space":"nowrap"}, `color: ${getColorQlt(data.qlt)}`])
              }, _toDisplayString(data.additionalInfo?.ptn ?? data.ptn), 5),
              (highlightMap.value[data.id])
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, " 👤 " + _toDisplayString(highlightMap.value[data.id].adminId), 1))
                : _createCommentVNode("", true)
            ], 2)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "score",
          header: "Score",
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _withDirectives((_openBlock(), _createElementBlock("span", null, [
              _createTextVNode(_toDisplayString(data.score.toFixed(1)), 1)
            ])), [
              [
                _directive_tooltip,
                _unref(artefactScoreTooltip)(data.scoreDetails),
                void 0,
                { bottom: true }
              ]
            ])
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "averagePrice",
          header: "Медиана"
        }, {
          body: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(_unref(formatPrice)(Number(data.averagePrice))), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "salePrice",
          header: "Цена продажи"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", {
              class: _normalizeClass({
              'row-highlight': highlightMapPrice.value[data.id]
            }),
              style: {"white-space":"nowrap","position":"relative"}
            }, [
              _createElementVNode("span", {
                onClick: ($event: any) => (copyToClipboard(data.salePrice, 'price'))
              }, _toDisplayString(_unref(formatPrice)(Number(data.salePrice))), 9, _hoisted_4),
              (highlightMapPrice.value[data.id])
                ? (_openBlock(), _createElementBlock("span", _hoisted_5, " 👤 " + _toDisplayString(highlightMapPrice.value[data.id].adminId), 1))
                : _createCommentVNode("", true)
            ], 2)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "currentElems",
          header: "Лоты"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.currentElems, (lot, index) => {
                return (_openBlock(), _createElementBlock("span", { key: index }, [
                  _createElementVNode("span", null, _toDisplayString(lot.price), 1),
                  (lot.additionalInfo?.ptn !== undefined)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[1] || (_cache[1] = _createTextVNode(" - ", -1)),
                        _createElementVNode("span", null, _toDisplayString(lot.additionalInfo.ptn), 1)
                      ], 64))
                    : _createCommentVNode("", true),
                  _cache[2] || (_cache[2] = _createTextVNode("; ", -1))
                ]))
              }), 128))
            ])
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "currentElems",
          header: "Время"
        }, {
          body: _withCtx(({ data }) => [
            (data.currentElems.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, _toDisplayString(formatMs(getAuctionTimer(data.startTime, data.endTime).passedMs)), 1),
                  _createElementVNode("div", _hoisted_8, _toDisplayString(formatMs(getAuctionTimer(data.startTime, data.endTime).leftMs)), 1)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), { header: "Продажи" }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(_unref(getRecentSales)(data, 24)) + " / 24ч", 1),
            _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(getRecentSales)(data, 48)) + " / 48ч", 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "historySalePrices",
          header: "Количество проданных"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(data.historySalePrices.length), 1),
            _createElementVNode("div", null, [
              _createVNode(HistoryChart, {
                historySalePrices: data.historySalePrices,
                "sale-price": data.salePrice
              }, null, 8, ["historySalePrices", "sale-price"])
            ])
          ]),
          _: 1
        }),
        (_ctx.isBidVariant)
          ? (_openBlock(), _createBlock(_unref(Column), {
              key: 0,
              field: "startPrice",
              header: "Текущая ставка"
            }, {
              body: _withCtx(({ data }) => [
                (data.isBid)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(_unref(formatPrice)(Number(data.startPrice))), 1))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_ctx.isBidVariant)
          ? (_openBlock(), _createBlock(_unref(Column), {
              key: 1,
              field: "startPrice",
              header: "Ставка на перебив"
            }, {
              body: _withCtx(({ data }) => [
                (data.isBid)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      onClick: ($event: any) => (copyToClipboard(calculatedBid(data)))
                    }, _toDisplayString(_unref(formatPrice)(Number(calculatedBid(data)))), 9, _hoisted_11))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(Column), {
          field: "profit",
          header: "Профит"
        }, {
          body: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(_unref(formatPrice)(Number(data.profit))), 1)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["value"])
  ]))
}
}

})